import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Application } from '../types';

interface ApplicationContextType {
  applications: Application[];
  addApplication: (application: Omit<Application, 'id' | 'applicationDate'>) => void;
  updateApplicationStatus: (id: string, status: Application['status']) => void;
  getApplicationsByStudent: (studentId: string) => Application[];
  getApplicationsByCourse: (courseId: string) => Application[];
  cancelApplication: (id: string) => void;
}

const ApplicationContext = createContext<ApplicationContextType | undefined>(undefined);

export const ApplicationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [applications, setApplications] = useState<Application[]>(() => {
    // localStorage에서 데이터 불러오기
    const savedApplications = localStorage.getItem('applications');
    if (savedApplications) {
      return JSON.parse(savedApplications);
    }
    return [];
  });

  // 데이터 변경 시 localStorage에 저장
  useEffect(() => {
    localStorage.setItem('applications', JSON.stringify(applications));
  }, [applications]);

  const addApplication = (applicationData: Omit<Application, 'id' | 'applicationDate'>) => {
    const newApplication: Application = {
      ...applicationData,
      id: Date.now().toString(),
      applicationDate: new Date().toISOString(),
      status: 'pending'
    };
    setApplications(prev => [...prev, newApplication]);
  };

  const updateApplicationStatus = (id: string, status: Application['status']) => {
    setApplications(prev => prev.map(app =>
      app.id === id ? { ...app, status } : app
    ));
  };

  const getApplicationsByStudent = (studentId: string) => {
    return applications.filter(app => app.studentId === studentId);
  };

  const getApplicationsByCourse = (courseId: string) => {
    return applications.filter(app => app.courseId === courseId);
  };

  const cancelApplication = (id: string) => {
    updateApplicationStatus(id, 'cancelled');
  };

  return (
    <ApplicationContext.Provider value={{
      applications,
      addApplication,
      updateApplicationStatus,
      getApplicationsByStudent,
      getApplicationsByCourse,
      cancelApplication
    }}>
      {children}
    </ApplicationContext.Provider>
  );
};

export const useApplications = () => {
  const context = useContext(ApplicationContext);
  if (context === undefined) {
    throw new Error('useApplications must be used within an ApplicationProvider');
  }
  return context;
};